import { useState } from "react";
import type {
  ApplicationPipelineStage,
  LeadPipelineStage
} from "api/crm-analytics";
import {
  AnalyticsCard,
  formatMinutes,
  type ShowingsAnalyticsCard
} from "views/analytics/constants";
import { ModuleDateRangeSelect } from "views/analytics/components/ModuleDateRangeSelect";
import {
  resolvePresetRange,
  type ModuleDateRangeValue
} from "views/analytics/components/moduleDateRange";
import { LeadsCreatedCard } from "views/analytics/components/LeadsCreatedCard";
import { ShowingsCard } from "views/analytics/components/ShowingsCard";
import { AverageMedianCard } from "views/analytics/components/AverageMedianCard";
import { PrescreeningFailuresCard } from "views/analytics/components/PrescreeningFailuresCard";
import { ListingViewsCard } from "views/analytics/components/ListingViewsCard";
import { LeadPipelineCard } from "views/analytics/components/LeadPipelineCard";
import { LeadsBySourceCard } from "views/analytics/components/LeadsBySourceCard";
import { ApplicationPipelineCard } from "views/analytics/components/ApplicationPipelineCard";
import { ApprovalsBySourceCard } from "views/analytics/components/ApprovalsBySourceCard";
import { LeadsCreatedModal } from "views/analytics/components/LeadsCreatedModal";
import { ShowingsDrilldownModal } from "views/analytics/components/ShowingsDrilldownModal";
import { DaysOnMarketModal } from "views/analytics/components/DaysOnMarketModal";
import { PrescreeningFailuresModal } from "views/analytics/components/PrescreeningFailuresModal";
import { TimeToManualContactModal } from "views/analytics/components/TimeToManualContactModal";
import { TouchPointsPerLeadModal } from "views/analytics/components/TouchPointsPerLeadModal";
import { LeadPipelineModal } from "views/analytics/components/LeadPipelineModal";
import { ApplicationPipelineModal } from "views/analytics/components/ApplicationPipelineModal";
import { useUnitAnalytics } from "./controller";

const DEFAULT_PRESET = "last_30_days";

const STAT_GRID_CLASSNAME =
  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4";

const CHART_PAIR_GRID_CLASSNAME =
  "grid grid-cols-1 xl:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)] gap-4";

const SHOWINGS_TILES: Array<{
  card: ShowingsAnalyticsCard;
  metric: "showings_scheduled" | "showings_completed" | "showings_no_shows";
}> = [
  { card: AnalyticsCard.SHOWINGS_SCHEDULED, metric: "showings_scheduled" },
  { card: AnalyticsCard.SHOWINGS_COMPLETED, metric: "showings_completed" },
  { card: AnalyticsCard.SHOWINGS_NO_SHOWS, metric: "showings_no_shows" }
];

export const AnalyticsTab = ({ unitId }: { unitId: number }) => {
  const [dateRange, setDateRange] = useState<ModuleDateRangeValue>(() => ({
    value: DEFAULT_PRESET,
    ...resolvePresetRange(DEFAULT_PRESET)
  }));
  const [activeCard, setActiveCard] = useState<AnalyticsCard | null>(null);
  const [leadStage, setLeadStage] = useState<LeadPipelineStage | null>(null);
  const [applicationStage, setApplicationStage] =
    useState<ApplicationPipelineStage | null>(null);

  const {
    unitParams,
    dateParams,
    dateLabel,
    chartStartDate,
    chartEndDate,
    overview,
    isOverviewLoading,
    listingViews,
    isListingViewsLoading,
    listingViewsColor,
    leadPipeline,
    isLeadPipelineLoading,
    sourceColorMap,
    leadsBySource,
    isLeadsBySourceLoading,
    applicationPipeline,
    isApplicationPipelineLoading,
    approvalsBySource,
    isApprovalsBySourceLoading
  } = useUnitAnalytics({ unitId, dateRange });

  const closeCard = () => setActiveCard(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <ModuleDateRangeSelect value={dateRange} onChange={setDateRange} />
      </div>

      <div className="space-y-4">
        <div className={STAT_GRID_CLASSNAME}>
          <LeadsCreatedCard
            count={overview?.leads_created}
            isLoading={isOverviewLoading}
            onClick={() => setActiveCard(AnalyticsCard.LEADS_CREATED)}
          />
          {SHOWINGS_TILES.map(({ card, metric }) => (
            <ShowingsCard
              key={card}
              title={card}
              count={overview?.[metric]}
              isLoading={isOverviewLoading}
              onClick={() => setActiveCard(card)}
            />
          ))}
        </div>

        <div className={STAT_GRID_CLASSNAME}>
          <AverageMedianCard
            type={AnalyticsCard.DAYS_ON_MARKET}
            average={overview?.days_on_market?.average ?? null}
            median={overview?.days_on_market?.median ?? null}
            isLoading={isOverviewLoading}
            onClick={() => setActiveCard(AnalyticsCard.DAYS_ON_MARKET)}
          />
          <PrescreeningFailuresCard
            count={overview?.prescreening_criteria_failures}
            isLoading={isOverviewLoading}
            onClick={() => setActiveCard(AnalyticsCard.PRESCREENING_FAILURES)}
          />
          <AverageMedianCard
            type={AnalyticsCard.TIME_TO_MANUAL_CONTACT}
            average={overview?.time_to_manual_contact?.average ?? null}
            median={overview?.time_to_manual_contact?.median ?? null}
            isLoading={isOverviewLoading}
            onClick={() => setActiveCard(AnalyticsCard.TIME_TO_MANUAL_CONTACT)}
            formatValue={formatMinutes}
          />
          <AverageMedianCard
            type={AnalyticsCard.TOUCH_POINTS_PER_LEAD}
            average={overview?.touch_points_per_lead?.average ?? null}
            median={overview?.touch_points_per_lead?.median ?? null}
            isLoading={isOverviewLoading}
            onClick={() => setActiveCard(AnalyticsCard.TOUCH_POINTS_PER_LEAD)}
          />
        </div>

        <ListingViewsCard
          data={listingViews}
          isLoading={isListingViewsLoading}
          color={listingViewsColor}
          startDate={chartStartDate}
          endDate={chartEndDate}
        />
      </div>

      <div className={CHART_PAIR_GRID_CLASSNAME}>
        <LeadPipelineCard
          data={leadPipeline}
          isLoading={isLeadPipelineLoading}
          dateLabel={dateLabel}
          sourceColorMap={sourceColorMap}
          onStageClick={setLeadStage}
        />
        <LeadsBySourceCard
          data={leadsBySource}
          isLoading={isLeadsBySourceLoading}
          dateLabel={dateLabel}
        />
      </div>

      <div className={CHART_PAIR_GRID_CLASSNAME}>
        <ApplicationPipelineCard
          data={applicationPipeline}
          isLoading={isApplicationPipelineLoading}
          dateLabel={dateLabel}
          onStageClick={setApplicationStage}
        />
        <ApprovalsBySourceCard
          data={approvalsBySource}
          isLoading={isApprovalsBySourceLoading}
          dateLabel={dateLabel}
        />
      </div>

      <LeadsCreatedModal
        opened={activeCard === AnalyticsCard.LEADS_CREATED}
        onClose={closeCard}
        dateParams={dateParams}
        filterParams={unitParams}
      />
      {SHOWINGS_TILES.map(({ card }) => (
        <ShowingsDrilldownModal
          key={card}
          card={card}
          opened={activeCard === card}
          onClose={closeCard}
          dateParams={dateParams}
          filterParams={unitParams}
        />
      ))}
      <DaysOnMarketModal
        opened={activeCard === AnalyticsCard.DAYS_ON_MARKET}
        onClose={closeCard}
        filterParams={unitParams}
      />
      <PrescreeningFailuresModal
        opened={activeCard === AnalyticsCard.PRESCREENING_FAILURES}
        onClose={closeCard}
        dateParams={dateParams}
        filterParams={unitParams}
      />
      <TimeToManualContactModal
        opened={activeCard === AnalyticsCard.TIME_TO_MANUAL_CONTACT}
        onClose={closeCard}
        start={dateParams.start_date}
        finish={dateParams.end_date}
        filterParams={unitParams}
      />
      <TouchPointsPerLeadModal
        opened={activeCard === AnalyticsCard.TOUCH_POINTS_PER_LEAD}
        onClose={closeCard}
        start={dateParams.start_date}
        finish={dateParams.end_date}
        filterParams={unitParams}
      />
      <LeadPipelineModal
        opened={leadStage !== null}
        onClose={() => setLeadStage(null)}
        stage={leadStage}
        dateParams={dateParams}
        filterParams={unitParams}
      />
      <ApplicationPipelineModal
        opened={applicationStage !== null}
        onClose={() => setApplicationStage(null)}
        stage={applicationStage}
        dateParams={dateParams}
        filterParams={unitParams}
      />
    </div>
  );
};
